import { KeyboardEventHandler, useEffect, useRef, useState } from "react"

import { IconPencil } from "@tabler/icons-react"

import { cn } from "@/lib/utils"

export interface EditableTextProps {
  initialValue: string
  onChange?: (value: string) => void
  placeholder?: string
  label: string
  error?: string

  className?: string
  placeholderClassName?: string
}

export const EditableText = ({
  initialValue = "",
  onChange,

  placeholder,
  label,
  error,
  className,
  placeholderClassName,
}: EditableTextProps) => {
  const [value, setValue] = useState(initialValue)
  const [isEditing, setEditing] = useState(false)
  const labelRef = useRef<HTMLSpanElement | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)

  const handleClick = () => {
    if (!isEditing) setEditing(true)
  }

  const handleKeyDown: KeyboardEventHandler = (event) => {
    if (event.key === "Enter") {
      event.preventDefault()
      if (isEditing) {
        labelRef.current?.blur()
      } else {
        setEditing(true)
      }
    }
  }

  const handleBlur = () => {
    setEditing(false)
    const newValue = labelRef.current?.innerText.replaceAll("\n", "") ?? ""
    setValue(newValue)
    if (newValue !== initialValue) {
      onChange?.(newValue)
    }
  }

  const showPlaceholder = !isEditing && !value && Boolean(placeholder)

  useEffect(
    function shiftFocusOnStartEdit() {
      if (isEditing) {
        labelRef.current?.focus()
      }
    },
    [isEditing],
  )

  const editingProps = isEditing
    ? {
        role: "textbox",
        contentEditable: true,
        onBlur: handleBlur,
        suppressContentEditableWarning: true,
      }
    : {}

  return (
    <div
      ref={rootRef}
      className={cn(
        "group grid w-fit grid-cols-[auto_auto] items-center gap-x-4 gap-y-1",
        className,
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      {...(!isEditing ? { role: "button" } : {})}
      aria-label={`Edit ${label}`}
    >
      <div
        className={cn(
          "flex h-full min-h-full rounded-md px-2 py-1 focus-within:outline-1",
          {
            "outline-1 outline-destructive": !!error,
            "focus-within:outline-secondary": !error,
            "min-w-9": isEditing,
          },
        )}
      >
        <span ref={labelRef} className="outline-hidden" {...editingProps}>
          {value}
        </span>
        <span
          className={cn(
            "text-grey-400",
            {
              /*
                Fixes a bug in Firefox where the input is not the right size when it's empty.
                This makes it so the placeholder is always rendered and takes space, but is not visible, that way the parent will have the correct height.
              */
              "w-0 overflow-hidden whitespace-nowrap": !showPlaceholder,
            },
            placeholderClassName,
          )}
        >
          {placeholder}
        </span>
      </div>
      <span className="rounded-full p-2 group-hover:bg-accent">
        <IconPencil size={20} />
      </span>
      {error ? <div className="px-2 text-body2 text-error-500">{error}</div> : null}
    </div>
  )
}
